import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useClientAuth } from "@/hooks/useClientAuth";
import { Mail, Shield, AlertCircle, CheckCircle } from "lucide-react";

const ClientAuthPage = () => {
  const { clientId } = useParams();
  const { sendMagicLink } = useClientAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    const result = await sendMagicLink(email);
    
    if (result.success) {
      setSuccess("Lien magique envoyé ! Vérifiez votre boîte mail.");
    } else {
      // Vérifier si c'est une erreur de domaine non autorisé
      const errorMessage = result.message || "";
      
      if (errorMessage.includes("Accès non autorisé") || 
          errorMessage.includes("n'est pas autorisée") ||
          errorMessage.includes("403") ||
          errorMessage.toLowerCase().includes("unauthorized")) {
        setError(
          "🚫 Accès non autorisé pour ce domaine email. " +
          "Contactez votre administrateur ou écrivez à ac@ailix.ai pour obtenir l'accès à cet espace client."
        );
      } else {
        setError(`Erreur: ${result.message || "Erreur inconnue"}`);
      }
    }
    
    setLoading(false);
  };

  const displayClientName = clientId ? clientId.charAt(0).toUpperCase() + clientId.slice(1).replace('-', ' ') : "Client";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            Accès sécurisé
          </h1>
          <p className="text-muted-foreground mt-2">
            Plateforme pour décupler votre potentiel avec l'IA générative - {displayClientName}
          </p>
        </div>

        <Card className="bg-gradient-card shadow-elegant">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Authentification client
            </CardTitle>
            <CardDescription>
              Saisissez votre email pour recevoir un lien d'accès sécurisé à votre espace de transformation digitale
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre.email@entreprise.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error}
                    {error.includes("🚫 Accès non autorisé") && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                        <div className="text-sm text-orange-800">
                          <div className="font-semibold mb-2">💡 Comment obtenir l'accès ?</div>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            <li><strong>Administrateur interne :</strong> Contactez votre administrateur IT</li>
                            <li><strong>Support Ailix :</strong> Écrivez à <strong>ac@ailix.ai</strong></li>
                            <li><strong>Email autorisé :</strong> Utilisez une adresse email autorisée pour ce client</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">{success}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                variant="hero"
                disabled={loading || !email}
              >
                {loading ? "Envoi en cours..." : "Recevoir le lien d'accès"}
              </Button>
            </form>

            {/* Informations de sécurité */}
            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-primary mb-1">Lien magique sécurisé</p>
                  <p className="text-muted-foreground">
                    Recevez un lien d'accès unique par email, valide 1 heure. 
                    Aucun mot de passe requis, accès direct et sécurisé à votre espace client.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>© 2025 Ailix Consulting - Automatisez le présent. Accélérez le futur. </p>
        </div>
      </div>
    </div>
  );
};

export default ClientAuthPage;
